import logger from "debug";
import { defineStore } from "pinia";
import { useViewerStore } from "./viewer";
const debug = logger("travvir:debut:nuxt:store:measurement.js");

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

export function marzipanoToDepthDegrees(yawRad, pitchRad) {
  let yawDeg = (yawRad * RAD2DEG) % 360;
  if (yawDeg > 180) yawDeg -= 360;
  if (yawDeg < -180) yawDeg += 360;

  let pitchDeg = -pitchRad * RAD2DEG;
  if (pitchDeg > 90) pitchDeg = 90;
  if (pitchDeg < -90) pitchDeg = -90;

  return { yawDeg, pitchDeg };
}

export function yawPitchDegToMarzipano(yawDeg, pitchDeg) {
  return {
    yaw: yawDeg * DEG2RAD,
    pitch: -pitchDeg * DEG2RAD,
  };
}

export function vec3ToMarzipano(x, y, z) {
  const norm = Math.hypot(x, y, z);
  if (norm < 1e-12) return { yaw: 0, pitch: 0 };

  const nz = Math.max(-1, Math.min(1, z / norm));
  const pitchDeg = Math.asin(nz) * RAD2DEG;
  const yawDeg = Math.atan2(x, y) * RAD2DEG;

  return yawPitchDegToMarzipano(yawDeg, pitchDeg);
}

export const useMeasurementStore = defineStore("measurement", {
  state: () => ({
    editMode: false,
    points: [],
    lines: [],
    cursorPos: null,
    cursorCoords: null,
    cursorSurface: null,
    depthMap: null,
    depthMapLoading: false,
    depthMapError: null,
    loadedDepthSceneId: null,
    depthLoadToken: 0,
    sceneDepthCache: {},
    hoveredPoint: null,
    scale: 1,
    distanceUnit: "m",
    showTutorial: true,
    show3DOverlay: false,
    switchingScene: false,
  }),

  getters: {
    //flag when depth are loaded case okk
    depthReadyForCurrentScene() {
      const viewerStore = useViewerStore();
      const sceneId = viewerStore.selectedSceneId;
      return (
        !!sceneId &&
        this.loadedDepthSceneId === sceneId &&
        !!this.depthMap?.is_loaded &&
        !this.depthMapLoading
      );
    },
  },

  actions: {
    stopTutorial(val = false) {
      this.showTutorial = val;
    },
    setHoveredPoint(pt) {
      this.hoveredPoint = pt ? { ...pt } : null;
    },
    getDisplayDistance(distance) {
      if (distance == null) return null;
      const convertedDistance =
        this.distanceUnit === "ft" ? distance * 3.28084 : distance;
      return Number.isFinite(convertedDistance) ? convertedDistance : null;
    },

    setDistanceUnit(unit) {
      this.distanceUnit = unit;
    },

    formatDistance(distance) {
      const val = this.getDisplayDistance(distance);
      if (val == null) return "–";

      if (this.distanceUnit === "ft") {
        const feet = Math.floor(val);
        const inches = Math.round((val - feet) * 12);
        return inches === 12 ? `${feet + 1}' 0"` : `${feet}' ${inches}"`;
      }
      return `${val.toFixed(2)} ${this.distanceUnit}`;
    },

    /**
     * Center of the depth panorama (yaw 0, pitch 0) and its last row.
     * Distances are rawDepth * scale, so s = h / t makes that span equal h.
     */
    applyDepthScale(queryScale, height) {
      const map = this.depthMap;
      if (!map?.is_loaded || !map.height) return;

      const lastPoint = map.height - 1;
      const bottomPitch = 90 - ((lastPoint + 0.5) / map.height) * 180;
      let center = null;
      let bottom = null;
      try {
        center = map.getSurface(0, 0);
        bottom = map.getSurface(0, bottomPitch);
        if (!center || !bottom) return;

        const p1 = { x: center.x, y: center.y, z: center.z };
        const p2 = { x: bottom.x, y: bottom.y, z: bottom.z };
        const coords = [p1.x, p1.y, p1.z, p2.x, p2.y, p2.z];
        if (!coords.every(Number.isFinite)) return;

        const t = Math.hypot(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
        if (!Number.isFinite(t) || t <= 1e-6) return;

        if (queryScale == null) {
          if (!Number.isFinite(height) || height <= 0) return;
          this.scale = height / t;
        }
        debug("[depth-query] depth span", { p1, p2, t, scale: this.scale });
      } catch (e) {
        debug("[depth-query] depth span error:", e);
      } finally {
        if (typeof center?.free === "function") center.free();
        if (typeof bottom?.free === "function") bottom.free();
      }
    },

    querySurface(yawRad, pitchRad) {
      if (!this.depthMap?.is_loaded) return null;
      const { yawDeg, pitchDeg } = marzipanoToDepthDegrees(yawRad, pitchRad);
      try {
        const s = this.depthMap.getSurface(yawDeg, pitchDeg);
        if (!s) return null;
        const result = {
          x: s.x * this.scale,
          y: s.y * this.scale,
          z: s.z * this.scale,
          nx: s.nx,
          ny: s.ny,
          nz: s.nz,
          depth: s.depth * this.scale,
          planarity: s.planarity,
          kind: s.kind,
        };
        if (typeof s.free === "function") s.free();
        return result;
      } catch (e) {
        debug("[depth-query] querySurface error:", e);
        return null;
      }
    },

    computeDistance(a, b) {
      if (!this.depthMap?.is_loaded) return null;
      const degA = marzipanoToDepthDegrees(a.yaw, a.pitch);
      const degB = marzipanoToDepthDegrees(b.yaw, b.pitch);
      try {
        return (
          this.depthMap.distance(
            degA.yawDeg,
            degA.pitchDeg,
            degB.yawDeg,
            degB.pitchDeg,
          ) * this.scale
        );
      } catch (e) {
        debug("[depth-query] distance error:", e);
        return null;
      }
    },

    addPoint(screenPos) {
      const viewerStore = useViewerStore();
      if (!viewerStore.viewer) return;
      const view = viewerStore.viewer.view();
      if (!view) return;

      let targetCoords;
      if (this.hoveredPoint?.coords) {
        targetCoords = { ...this.hoveredPoint.coords };
      } else {
        // Fallback snap check against existing endpoints
        const snapThresholdPx = 20;
        let closestSnap = null;
        let minDist = Infinity;
        for (const line of this.lines) {
          for (const p of [line.p1, line.p2]) {
            if (!p) continue;
            const s = view.coordinatesToScreen(p);
            if (s && Number.isFinite(s.x) && Number.isFinite(s.y)) {
              const d = Math.hypot(s.x - screenPos.x, s.y - screenPos.y);
              if (d <= snapThresholdPx && d < minDist) {
                minDist = d;
                closestSnap = { yaw: p.yaw, pitch: p.pitch };
              }
            }
          }
        }

        if (closestSnap) {
          targetCoords = closestSnap;
        } else {
          const coords = view.screenToCoordinates(screenPos);
          if (!coords) return;
          targetCoords = { yaw: coords.yaw, pitch: coords.pitch };
        }
      }

      if (this.points.length === 0) {
        this.points = [targetCoords];
        this.showTutorial = false;
        return;
      }

      const first = this.points[0];
      const isSamePoint =
        Math.hypot(
          first.yaw - targetCoords.yaw,
          first.pitch - targetCoords.pitch,
        ) < 1e-4;
      if (isSamePoint) {
        return;
      }

      const dist = this.computeDistance(first, targetCoords);
      this.lines.push({
        id: Date.now() + Math.random(),
        p1: { yaw: first.yaw, pitch: first.pitch },
        p2: { yaw: targetCoords.yaw, pitch: targetCoords.pitch },
        distance: dist,
      });
      this.points = [];
    },

    async loadDepthForScene(sceneId, depthUrl, options) {
      // Guard: skip on server-side or missing inputs
      if (import.meta.server || typeof window === "undefined" || !sceneId)
        return;

      // Cache hit: scene already loaded, just show the overlay
      if (this._isDepthAlreadyLoaded(sceneId)) {
        this.show3DOverlay = true;
        this.depthMapLoading = false;
        if (options?.height)
          this.applyDepthScale(options.scale, options.height);
        return;
      }

      // Race-condition token: cancels stale loads if a newer one starts
      const token = ++this.depthLoadToken;
      this._resetDepthState();

      try {
        const { DepthMap } = await import("~/pkg/depth_query.js");
        if (this._isTokenStale(token)) return;

        this.depthMap ??= new DepthMap();
        const bytes = await this._getDepthBytes(sceneId, depthUrl);
        if (this._isTokenStale(token)) return;

        // Abort if user has switched to a different scene
        if (useViewerStore().selectedSceneId !== sceneId) return;

        this.depthMap.loadNpy(bytes);
        this.loadedDepthSceneId = sceneId;
        this.show3DOverlay = true;

        if (options?.height)
          this.applyDepthScale(options.scale, options.height);

        debug("[depth-query] depth map loaded", {
          url: depthUrl,
          sceneId,
          width: this.depthMap.width,
          height: this.depthMap.height,
        });
      } catch (e) {
        if (this._isTokenStale(token)) return;
        debug("[depth-query] failed to load depth map:", e);
        this.depthMapError = String(e);
        this.loadedDepthSceneId = null;
        this.show3DOverlay = false;
      } finally {
        if (token === this.depthLoadToken) {
          this.depthMapLoading = false;
          if (this.show3DOverlay && this.cursorPos)
            this.setCursor(this.cursorPos);
        }
      }
    },


    _isDepthAlreadyLoaded(sceneId) {
      return (
        this.loadedDepthSceneId === sceneId &&
        this.depthMap?.is_loaded &&
        !this.depthMapLoading
      );
    },

    _resetDepthState() {
      this.depthMapLoading = true;
      this.show3DOverlay = false;
      this.cursorSurface = null;
      this.depthMapError = null;
    },

    _isTokenStale(token) {
      return token !== this.depthLoadToken;
    },

    async _getDepthBytes(sceneId, url) {
      if (this.sceneDepthCache[sceneId]) {
        debug("[depth-query] depth cache hit", { sceneId });
        return this.sceneDepthCache[sceneId];
      }

      const resp = await fetch(url);
      if (resp.status !== 200) throw new Error(`HTTP ${resp.status}: ${url}`);
      const bytes = new Uint8Array(await resp.arrayBuffer());
      this.sceneDepthCache[sceneId] = bytes;
      return bytes;
    },

    setCursor(pos) {
      this.cursorPos = pos ? { ...pos } : null;
      if (!pos) {
        this.cursorCoords = null;
        this.cursorSurface = null;
        this.hoveredPoint = null;
        return;
      }

      const viewerStore = useViewerStore();
      if (!viewerStore.viewer) return;

      const coords = viewerStore.viewer.view()?.screenToCoordinates(pos);
      if (coords) {
        this.cursorCoords = coords;
        if (this.depthReadyForCurrentScene) {
          this.cursorSurface = this.querySurface(coords.yaw, coords.pitch);
        } else {
          this.cursorSurface = null;
        }
      } else {
        this.cursorCoords = null;
        this.cursorSurface = null;
      }
    },

    deleteLine(index) {
      this.lines.splice(index, 1);
    },

    clearAll() {
      this.points = [];
      this.lines = [];
      this.cursorPos = null;
      this.cursorCoords = null;
      this.cursorSurface = null;
      this.hoveredPoint = null;
      this.showTutorial = true;
    },

    updateCursor() {
      if (this.cursorPos && this.editMode) {
        this.setCursor(this.cursorPos);
      }
    },

    finishSession() {
      this.points = [];
      this.cursorPos = null;
      this.cursorCoords = null;
      this.cursorSurface = null;
      this.hoveredPoint = null;
    },

    enterEditMode() {
      this.editMode = true;
      if (this.lines.length > 0) {
        this.showTutorial = false;
      }
      const viewerStore = useViewerStore();
      if (viewerStore.viewer) {
        viewerStore.viewer.stopMovement();
        // Do NOT disable controls — the user should still be able to
        // drag/pan the panorama while placing measurement points.
        // Marzipano distinguishes a click (place point) from a drag (pan).
        viewerStore.viewer.setIdleMovement(Infinity); // pause autorotation
      }
    },

    exitEditMode() {
      // Do NOT clear lines here so confirmed measurements stay stored and visible
      this.finishSession();
      this.editMode = false;
      if (this.lines.length === 0) {
        this.showTutorial = true;
      } else {
        this.showTutorial = false;
      }
      // Re-enable the Marzipano viewer controls that were disabled on enter.
      // This MUST happen here so every exit path (Overlay X button, Toolbar,
      // etc.) always unfreezes the panorama and restores keyboard navigation.
      const viewerStore = useViewerStore();
      if (viewerStore.viewer) {
        viewerStore.viewer.controls().enable();
        viewerStore.viewer.controls().enableMethod("leftArrowKey");
        viewerStore.viewer.controls().enableMethod("rightArrowKey");
        // viewerStore.viewer.controls().enableMethod("upArrowKey");
        // viewerStore.viewer.controls().enableMethod("downArrowKey");
        // viewerStore.viewer.controls().enableMethodGroup("plusMinusKeys");
        viewerStore.viewer.setIdleMovement(5000, viewerStore.autorotate);
      }
    },
  },
});
